import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import {
    TextField, MenuItem, Select, FormControl, FormControlLabel,
    Checkbox, Avatar, Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import {RootState} from "../../../store";
import {setProfileData, setSearchVisibility} from "../../profile/profileSlice";
import {makeLink} from "../../common/languages-helper";
import {CountriesList} from "../../common/countries-list";
import api from "../../api/api";
import {fetchCompositeProfile} from "../../../hooks/usePreferences";
import {toast} from "react-toastify";
import YellowButton from "../../common/buttons/YellowButton";
import SecondaryButton from "../../common/buttons/SecondaryButton";
import Footer from "../../navigation/Footer";
import {EXTERNAL_LINKS} from "../../../config/external-links";
import "./ProfileSettings.css";
import {trackCtaClick} from "../../../analytics/events";

type SectionId = "basic" | "therapist" | "session" | "password" | "plan" | "billing" | "marketing" | "advert";

const inputSx = {
    "& .MuiOutlinedInput-root": {borderRadius: "12px", backgroundColor: "#F8FBFC"},
};

const ProfileSettings: React.FC = () => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const profile = useSelector((state: RootState) => state.profile);

    const [section, setSection] = useState<SectionId>("basic");
    const [saving, setSaving] = useState(false);
    // The WP-owned directory listing (name, bio, city, rates, visibility, ...),
    // read-only in the app and edited on the WordPress site. null = no listing.
    const [directory, setDirectory] = useState<Record<string, any> | null>(null);

    // Server-backed fields kept in local form state.
    const [server, setServer] = useState({
        name: profile.name,
        phone: profile.phone,
        invoiceToCompany: profile.invoiceToCompany,
        companyName: profile.companyName,
        companyNIP: profile.companyNIP,
        companyEmail: profile.companyEmail,
        companyCountry: profile.companyCountry || "PL",
        companyAddress: profile.companyAddress,
        marketingEmails: profile.marketingEmails,
    });
    const onServer = (k: keyof typeof server, v: any) => setServer((s) => ({...s, [k]: v}));

    // Hydrate searchVisible from the composite profile (authoritative). This
    // field is WordPress-owned and federated by the API — read it from the
    // merged profile, not the app preferences bag.
    useEffect(() => {
        fetchCompositeProfile()
            .then(({ directory }) => {
                setDirectory(directory);
                // null / non-published listing => not visible. Coerce to a boolean
                // and sync redux so the dashboard never shows a stale value.
                const visible = directory?.searchVisible === true;
                if (visible !== profile.searchVisible) {
                    dispatch(setSearchVisibility(visible));
                }
            })
            .catch(() => {/* offline / unauthenticated / WP unavailable: keep local value */});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSave = async () => {
        setSaving(true);
        try {
            await api().patch("users/profile", {
                name: server.name,
                phone: server.phone ?? "",
                companyName: server.companyName ?? "",
                companyNIP: server.companyNIP ?? "",
                companyAddress: server.companyAddress ?? "",
                companyCountry: server.companyCountry ?? "PL",
                companyEmail: server.companyEmail ?? "",
                agreement: profile.agreement,
                marketingEmails: !!server.marketingEmails,
                invoiceToCompany: !!server.invoiceToCompany,
            });
            dispatch(setProfileData({
                _id: profile._id,
                externalId: profile.externalId,
                name: server.name,
                email: profile.email,
                phone: server.phone ?? "",
                role: profile.role,
                companyNIP: server.companyNIP ?? "",
                companyCountry: server.companyCountry ?? "",
                companyAddress: server.companyAddress ?? "",
                companyName: server.companyName ?? "",
                companyEmail: server.companyEmail ?? "",
                agreement: profile.agreement,
                invoiceToCompany: server.invoiceToCompany,
                marketingEmails: server.marketingEmails,
                deleteRequestSubmitted: profile.deleteRequestSubmitted,
            }));
            toast.success(t("Profile has been updated successfully"));
        } catch {
            toast.error(t("Failed to update profile. Please try again."));
        } finally {
            setSaving(false);
        }
    };

    // Sections whose data is owned by WordPress and edited there — hidden from
    // the app for now (code kept behind the flag). Basics + Profil terapeuty are
    // shown read-only with an "Edit on the EMDRlink website" link.
    const SHOW_LEGACY_EDIT_SECTIONS = false;
    const navItems: {id: SectionId; label: string}[] = [
        {id: "basic", label: t("Basic user data")},
        {id: "session", label: t("Session settings")},
        {id: "plan", label: t("Plan (Subscription)")},
        {id: "advert", label: t("Your advert on our website")},
        ...(SHOW_LEGACY_EDIT_SECTIONS ? [
            {id: "password" as SectionId, label: t("Change password")},
            {id: "billing" as SectionId, label: t("Billing data")},
            {id: "marketing" as SectionId, label: t("Marketing consents")},
        ] : []),
    ];

    // The app no longer writes profile/directory data (WordPress owns it); the
    // only legacy write path is the flagged-off billing/marketing forms.
    const canSave = SHOW_LEGACY_EDIT_SECTIONS && ["billing", "marketing"].includes(section);

    return (
        <>
        <div className="settings">
            <div className="settings-topbar">
                <span className="settings-title">{t("Account settings")}</span>
                <a className="settings-back" href={makeLink("/")}>
                    <ArrowBackIcon sx={{fontSize: 18}} /> {t("Back to profile")}
                </a>
            </div>

            <div className="settings-body">
                <nav className="settings-nav">
                    {navItems.map((item) => (
                        <button
                            key={item.id}
                            className={"settings-nav-item" + (section === item.id ? " active" : "")}
                            onClick={() => setSection(item.id)}
                            aria-current={section === item.id}
                        >
                            {item.label}
                        </button>
                    ))}
                </nav>

                <section className="settings-panel">
                    {section === "basic" && (
                        <>
                            <div className="settings-section-title">{t("Basic user data")}</div>
                            <div className="settings-section-hint">
                                {t("These details come from your therapist profile and are edited on the EMDRlink website.")}
                            </div>

                            <div className="settings-basic-grid">
                                <div className="settings-avatar-col">
                                    <span className="settings-field-label">{t("Profile photo")}</span>
                                    <Avatar src={directory?.photoUrl || profile.avatarUrl || undefined} sx={{width: 120, height: 120, bgcolor: "#0e3c47", fontSize: 40}}>
                                        {(directory?.name || profile.name || "").charAt(0).toUpperCase()}
                                    </Avatar>
                                </div>
                                <div className="settings-fields-col">
                                    <ReadOnlyField t={t} label={t("Name")} value={directory?.name || profile.name} />
                                    <ReadOnlyField t={t} label={t("Phone number")} value={directory?.phone || profile.phone} />
                                    <div className="settings-field">
                                        <span className="settings-field-label">{t("Profile in therapist search")}</span>
                                        <div>
                                            <span className={"settings-visibility-pill " + (directory?.searchVisible ? "on" : "off")}>
                                                {directory?.searchVisible ? t("Visible") : t("Not visible")}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="settings-fields-col">
                                    <ReadOnlyField t={t} label={t("Account email address")} value={profile.email} />
                                </div>
                            </div>
                            <EditInWp t={t} />
                        </>
                    )}

                    {section === "billing" && (
                        <>
                            <div className="settings-section-title">{t("Billing data")}</div>
                            <div className="settings-section-hint">{t("Used for invoicing your subscription.")}</div>
                            <div className="settings-group">
                                <FormControlLabel label={t("Invoice to company")} control={
                                    <Checkbox checked={server.invoiceToCompany}
                                        onChange={(e) => onServer("invoiceToCompany", e.target.checked)} />
                                } />
                                {server.invoiceToCompany && (
                                    <>
                                        <div className="settings-row">
                                            <div>
                                                <span className="settings-field-label">{t("Company Name")}</span>
                                                <TextField fullWidth size="small" sx={inputSx}
                                                    value={server.companyName} onChange={(e) => onServer("companyName", e.target.value)} />
                                            </div>
                                            <div>
                                                <span className="settings-field-label">{t("Company NIP (VAT)")}</span>
                                                <TextField fullWidth size="small" sx={inputSx}
                                                    value={server.companyNIP} onChange={(e) => onServer("companyNIP", e.target.value)} />
                                            </div>
                                        </div>
                                        <div className="settings-row">
                                            <div>
                                                <span className="settings-field-label">{t("Company email (optional)")}</span>
                                                <TextField fullWidth size="small" sx={inputSx}
                                                    value={server.companyEmail} onChange={(e) => onServer("companyEmail", e.target.value)} />
                                            </div>
                                            <div>
                                                <span className="settings-field-label">{t("Company country")}</span>
                                                <FormControl fullWidth>
                                                    <Select value={server.companyCountry} size="small"
                                                        onChange={(e) => onServer("companyCountry", e.target.value)}
                                                        sx={{borderRadius: "12px", backgroundColor: "#F8FBFC"}}>
                                                        {Object.entries(CountriesList).map(([code, name]) => (
                                                            <MenuItem key={code} value={code}>{name}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </div>
                                        </div>
                                        <div className="settings-row single">
                                            <div>
                                                <span className="settings-field-label">{t("Company address (optional)")}</span>
                                                <TextField fullWidth size="small" sx={inputSx}
                                                    value={server.companyAddress} onChange={(e) => onServer("companyAddress", e.target.value)} />
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </>
                    )}

                    {section === "marketing" && (
                        <>
                            <div className="settings-section-title">{t("Marketing consents")}</div>
                            <div className="settings-group">
                                <FormControlLabel label={t("Send me feature updates and marketing emails")} control={
                                    <Checkbox checked={server.marketingEmails}
                                        onChange={(e) => onServer("marketingEmails", e.target.checked)} />
                                } />
                            </div>
                        </>
                    )}

                    {section === "plan" && (
                        <>
                            <div className="settings-section-title">{t("Plan (Subscription)")}</div>
                            <div className="settings-group" style={{textAlign: "center"}}>
                                <Chip label={t("Free account")} sx={{bgcolor: "#EFF8FA", color: "#0E3C47", fontWeight: 600, mb: 2}} />
                                <div className="settings-section-hint">
                                    {t("Upgrade to unlock group sessions, website & calendar integrations and more.")}
                                </div>
                                <YellowButton onClick={() => { trackCtaClick("upgrade_plan"); window.open(EXTERNAL_LINKS.pricing, "_blank", "noopener,noreferrer"); }}>
                                    {t("Upgrade plan")}
                                </YellowButton>
                            </div>
                        </>
                    )}

                    {section === "advert" && (
                        <>
                            <div className="settings-section-title">{t("Your advert on our website")}</div>
                            <div className="settings-section-hint">
                                {t("Place your banner on our site and promote yourself in the therapist search results. Be visible — even for 5x more patients.")}
                            </div>
                            <div className="settings-advert-preview">
                                <img
                                    src={`${process.env.PUBLIC_URL}/images/advert-banner-example.png`}
                                    alt={t("Example of a therapist advert banner on the EMDRlink website")}
                                    onError={(e) => {(e.currentTarget.style.display = "none");}}
                                />
                            </div>
                            <div className="settings-section-hint">
                                {t("If you're interested, write to us at:")}{" "}
                                <a href="mailto:reklama@emdrlink.pl">reklama@emdrlink.pl</a>{" "}
                                {t("and we'll arrange the details.")}
                            </div>
                        </>
                    )}

                    {(section === "session" || section === "password") && (
                        <>
                            <div className="settings-section-title">
                                {section === "session" && t("Session settings")}
                                {section === "password" && t("Change password")}
                            </div>
                            <div className="settings-coming-soon">{t("This section is coming soon.")}</div>
                        </>
                    )}

                    {canSave && (
                        <div className="settings-actions">
                            <YellowButton onClick={handleSave} disabled={saving}>
                                {saving ? t("Saving…") : t("Save changes")}
                            </YellowButton>
                        </div>
                    )}
                </section>
            </div>
        </div>
        <Footer />
        </>
    );
};

// A single read-only field styled like a disabled input (design-faithful).
const ReadOnlyField: React.FC<{label: string; value: any; t: any}> = ({label, value, t}) => (
    <div className="settings-field">
        <span className="settings-field-label">{label}</span>
        <div className="settings-readonly-value">
            {value ? value : <span style={{color: "#9aa4ac"}}>{t("Not provided")}</span>}
        </div>
    </div>
);

// Directory/profile data is owned by WordPress; editing happens there.
const EditInWp: React.FC<{t: any}> = ({t}) => (
    <div className="settings-actions">
        <SecondaryButton onClick={() => { trackCtaClick("edit_profile_on_website"); window.open(EXTERNAL_LINKS.customerProfile, "_blank", "noopener,noreferrer"); }}>
            {t("Edit on the EMDRlink website")}
        </SecondaryButton>
    </div>
);

export default ProfileSettings;
